import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input, Label } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast, Toaster } from "sonner";
import ReadinessCard from "../components/dashboard/ReadinessCard";
import {
  Sparkles, Upload, FileText, CheckCircle2, XCircle, AlertTriangle, Wand2, ShieldCheck, Lightbulb, Trash2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const STEPS = [
  { key: "upload", label: "Upload policy", icon: Upload },
  { key: "extract", label: "Extract text", icon: FileText },
  { key: "analyze", label: "Analyze controls", icon: Wand2 },
  { key: "missing", label: "Identify gaps", icon: XCircle },
  { key: "risk", label: "Risk summary", icon: AlertTriangle },
  { key: "recommend", label: "Recommendations", icon: Lightbulb },
  { key: "readiness", label: "Readiness score", icon: ShieldCheck },
];

const priVariant = { critical: "critical", high: "warning", medium: "warning", low: "success" };

export default function AdvisorPage() {
  const [analyses, setAnalyses] = useState([]);
  const [current, setCurrent] = useState(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(-1);
  const fileRef = useRef();

  const load = async () => {
    const { data } = await api.get("/advisor/analyses");
    setAnalyses(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const runAnalysis = async () => {
    if (!file) return toast.error("Please choose a policy file");
    setRunning(true);
    // Animate workflow steps
    for (let i = 0; i < STEPS.length - 1; i++) {
      setStep(i);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 350));
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (title) fd.append("title", title);
      const { data } = await api.post("/advisor/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStep(STEPS.length - 1);
      await new Promise((r) => setTimeout(r, 300));
      setCurrent(data);
      toast.success("Analysis complete");
      setTitle("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Analysis failed");
    } finally {
      setRunning(false);
      setStep(-1);
    }
  };

  const open = async (a) => {
    const { data } = await api.get(`/advisor/analyses/${a.id}`);
    setCurrent(data);
  };
  const del = async (a) => {
    if (!window.confirm("Delete this analysis?")) return;
    await api.delete(`/advisor/analyses/${a.id}`);
    load();
    if (current?.id === a.id) setCurrent(null);
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />

      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
          <Sparkles className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">AI Compliance Advisor</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Upload a policy document. The advisor extracts text, compares it against the control library, and produces a coverage analysis, risk summary, targeted recommendations and a projected audit-readiness score.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1" data-testid="advisor-upload-card">
          <CardHeader>
            <CardTitle>Analyze a policy</CardTitle>
            <CardDescription>Supported: PDF, DOCX, TXT — max 10 MB.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Analysis title (optional)</Label>
              <Input data-testid="advisor-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5" placeholder="e.g. InfoSec Policy v3.2" />
            </div>
            <div>
              <Label>Policy file</Label>
              <input
                ref={fileRef}
                data-testid="advisor-file"
                type="file"
                accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1.5 block w-full text-sm file:mr-3 file:h-10 file:px-4 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:font-medium hover:file:bg-primary-hover"
              />
              {file && <div className="text-xs text-muted-foreground mt-1">{file.name} · {Math.round(file.size / 1024)} KB</div>}
            </div>
            <Button
              className="w-full"
              onClick={runAnalysis}
              disabled={running || !file}
              data-testid="advisor-run"
            >
              <Sparkles className="w-4 h-4" /> {running ? "Analyzing…" : "Run analysis"}
            </Button>

            {/* Workflow steps */}
            {(running || step >= 0) && (
              <div className="border-t border-border pt-4">
                <div className="label-eyebrow mb-3">Workflow</div>
                <ul className="space-y-2">
                  {STEPS.map((s, idx) => {
                    const Icon = s.icon;
                    const done = idx < step || (idx === STEPS.length - 1 && step === STEPS.length - 1);
                    const active = idx === step;
                    return (
                      <li key={s.key} className="flex items-center gap-2 text-sm">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                          done ? "bg-green-100 text-green-700 dark:bg-green-950/40" :
                          active ? "bg-primary-subtle text-primary animate-pulse" :
                          "bg-muted text-muted-foreground"
                        }`}>
                          {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                        </span>
                        <span className={done ? "text-foreground" : active ? "text-primary font-medium" : "text-muted-foreground"}>
                          {s.label}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2" data-testid="advisor-history">
          <CardHeader>
            <CardTitle>Previous analyses</CardTitle>
            <CardDescription>Click any analysis to view its full results below.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {analyses.length === 0 && (
                <li className="px-6 py-8 text-center text-sm text-muted-foreground">
                  No analyses yet. Upload your first policy on the left.
                </li>
              )}
              {analyses.map((a) => (
                <li key={a.id} data-testid={`analysis-row-${a.id}`} className="px-6 py-3 flex items-center gap-3 hover:bg-muted/40 transition-colors cursor-pointer" onClick={() => open(a)}>
                  <div className="w-9 h-9 rounded-md bg-primary-subtle text-primary flex items-center justify-center shrink-0 dark:bg-primary/15">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{a.title}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {a.filename} · by {a.analyzed_by_name} · {a.analyzed_at && formatDistanceToNow(new Date(a.analyzed_at), { addSuffix: true })}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold tabular-nums">{a.coverage_pct}%</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">coverage</div>
                  </div>
                  <Button size="icon" variant="destructive" onClick={(e) => { e.stopPropagation(); del(a); }}><Trash2 className="w-4 h-4" /></Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {current && (
        <div className="space-y-4" data-testid="advisor-result">
          <div className="flex items-center gap-3">
            <div className="text-lg font-semibold">Results: {current.title}</div>
            <Badge variant="primary">{current.filename}</Badge>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <Card>
                <CardHeader><CardTitle>Compliance summary</CardTitle></CardHeader>
                <CardContent><p className="text-sm">{current.compliance_summary}</p></CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Risk summary</CardTitle><CardDescription>Weighted by missing control priorities.</CardDescription></CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 mb-3">
                    <Badge variant={current.risk_load_pct >= 60 ? "critical" : current.risk_load_pct >= 30 ? "warning" : "success"}>
                      Risk load: {current.risk_load_pct}%
                    </Badge>
                    <span className="text-sm">{current.risk_summary}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className={`h-full ${
                      current.risk_load_pct >= 60 ? "bg-red-600" : current.risk_load_pct >= 30 ? "bg-amber-500" : "bg-green-500"
                    }`} style={{ width: `${current.risk_load_pct}%` }} />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Framework coverage</CardTitle><CardDescription>Detected controls per framework.</CardDescription></CardHeader>
                <CardContent className="p-0">
                  <ul className="divide-y divide-border">
                    {(current.framework_summary || []).map((f) => (
                      <li key={f.id} className="px-6 py-3">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-medium">{f.name}</span>
                          <span className="tabular-nums">{f.detected}/{f.total} · {f.coverage}%</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-primary" style={{ width: `${f.coverage}%` }} />
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Missing controls & recommendations</CardTitle>
                  <CardDescription>Prioritized by criticality of each missing control.</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <ul className="divide-y divide-border">
                    {(current.recommendations || []).length === 0 && (
                      <li className="px-6 py-6 text-center text-sm text-muted-foreground">
                        No missing controls — the policy is comprehensive!
                      </li>
                    )}
                    {(current.recommendations || []).map((r) => (
                      <li key={r.control_id} className="px-6 py-4">
                        <div className="flex items-center gap-2 mb-1">
                          <XCircle className="w-4 h-4 text-red-600" />
                          <span className="text-sm font-medium">{r.control_title}</span>
                          <Badge variant={priVariant[r.priority]}>{r.priority}</Badge>
                        </div>
                        <div className="text-sm text-muted-foreground pl-6">
                          <Lightbulb className="w-3 h-3 inline mr-1" /> {r.recommendation}
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Detected controls</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {(current.controls || []).filter((c) => c.detected).map((c) => (
                      <span key={c.id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-green-500/40 bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300">
                        <CheckCircle2 className="w-3 h-3" /> {c.title}
                      </span>
                    ))}
                    {(current.controls || []).filter((c) => c.detected).length === 0 && (
                      <span className="text-sm text-muted-foreground">No controls detected.</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="lg:col-span-1">
              <ReadinessCard data={current.readiness_snapshot} testId="advisor-readiness" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
